import { AlertTriangle } from 'lucide-react';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { type CostableLine } from '@/lib/sales/document-costing';
import { documentMargin, preTaxRevenue } from '@/lib/sales/document-margin-summary';
import { cn } from '@/lib/utils';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function Row({
  label,
  value,
  muted,
  strong,
}: {
  label: string;
  value: string;
  muted?: boolean;
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 text-sm',
        muted && 'text-muted-foreground',
        strong && 'font-medium',
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/**
 * WHETHER THIS PANEL RENDERS ANYTHING AT ALL — asked once, here, so a layout
 * built around the panel cannot disagree with the panel.
 *
 * `DocumentLineItemsCard` reserves a fixed 280px track for it. If that track
 * were decided by "were cost props passed?" while the panel decided its own
 * visibility separately, the two would disagree the moment a caller combined
 * `showPriceSummary={false}` with a masked user: a narrowed line editor beside
 * a dead gutter. Exporting the predicate keeps the panel the single authority
 * and leaves every caller with nothing to remember.
 *
 * A hook rather than a plain function because the answer depends on the
 * viewer's field masks, which live in a store.
 */
export function useDocumentCostPanelSuppressed(showPriceSummary = true): boolean {
  // Cost and margin are all this panel would have left to say; without the
  // price rows there is no reason to draw the frame.
  return useIsFieldHidden('PROFITABILITY') && !showPriceSummary;
}

/**
 * What the document sells for, what it costs us, and the margin between them.
 *
 * Deliberately alongside the line items rather than in the customer-facing
 * totals: every figure here is internal, and the operator needs to see margin
 * move as they price the job, not after saving it.
 */
export function DocumentCostPanel({
  lineItems,
  pricingMode = 'STANDARD',
  subtotal,
  taxTotal,
  grandTotal,
  breakdown,
  showPriceSummary = true,
  className,
}: {
  lineItems?: CostableLine[];
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  subtotal: number;
  taxTotal: number;
  grandTotal: number;
  /**
   * Cost figures worked out by the caller.
   *
   * The pricebook prices one service from its own labor and parts rather than
   * from document lines, so it supplies the numbers directly — the panel is the
   * same either way, because an operator comparing the two should not have to
   * read two different layouts of the same idea.
   */
  breakdown?: {
    labor: number;
    laborHours?: number;
    /** Of `labor`, the part paid as a share of the margin. */
    laborFromMargin?: number;
    /** See `DocumentCosting.laborSharePercent`. */
    laborSharePercent?: number | null;
    material: number;
    equipment: number;
  };
  /**
   * Whether to print the PRICE rows at the top.
   *
   * False where the caller already presents the price and its composition — the
   * pricebook states "Service price / Parts / Billed extra / Total" itself, and
   * restating the same money here put an internal figure (margin) in the middle
   * of the customer-facing one. What the customer PAYS and what we KEEP are
   * different kinds of statement and read as one list when interleaved.
   *
   * Margin is still computed from the pre-tax total: not printing the price
   * does not mean not knowing it.
   */
  showPriceSummary?: boolean;
  className?: string;
}) {
  // Margin is measured against what the business KEEPS, and sales tax is money
  // collected for the state — counting it as revenue inflated both figures
  // (NOT-264, the same correction the job financials card already carries).
  //
  // Derived from `grandTotal` rather than read off `subtotal`, because those two
  // props answer different questions: `subtotal` is the pricing engine's
  // PRE-discount sum, so using it would credit the business with money it gave
  // away. `grandTotal - taxTotal` is the post-discount, pre-tax net whatever the
  // caller's discounting looks like.
  const preTaxTotal = preTaxRevenue(grandTotal, taxTotal);

  // A COST PANEL WITH THE COSTS STRIPPED OUT IS NOT A COST PANEL — it is a
  // wrong one. PROFITABILITY removes `costBasis`, `laborCostRate`,
  // `laborCostLines` and `partCostSources` from every line before they reach
  // the browser, so every row below would read $0.00 and the margin would
  // print +100% on work that barely breaks even. Saying nothing is the honest
  // answer; the price rows above are PRICING and stay, because that is a
  // different mask and this user may well hold it.
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  const suppressed = useDocumentCostPanelSuppressed(showPriceSummary);

  const result = breakdown
    ? (() => {
        const totalCost = breakdown.labor + breakdown.material + breakdown.equipment;
        // Before labor, on the same terms `marginFor` states it: the parts are
        // spent, the crew is not yet paid.
        const contributionAmount = preTaxTotal - breakdown.material - breakdown.equipment;
        const netMarginAmount = preTaxTotal - totalCost;
        return {
          labor: breakdown.labor,
          laborFromMargin: breakdown.laborFromMargin ?? 0,
          laborSharePercent: breakdown.laborSharePercent ?? null,
          laborHours: breakdown.laborHours ?? 0,
          material: breakdown.material,
          equipment: breakdown.equipment,
          totalCost,
          hoursMissingCostRate: 0,
          priceTotal: preTaxTotal,
          contributionAmount,
          contributionPercent: preTaxTotal > 0 ? (contributionAmount / preTaxTotal) * 100 : null,
          netMarginAmount,
          netMarginPercent: preTaxTotal > 0 ? (netMarginAmount / preTaxTotal) * 100 : null,
        };
      })()
    : documentMargin(lineItems ?? [], { grandTotal, taxTotal }, pricingMode);

  // Margin is only meaningful once something is priced.
  const margin = result.netMarginPercent;
  const marginTone =
    margin == null ? 'text-muted-foreground' : margin < 0 ? 'text-destructive' : 'text-success';

  // Nothing left to say: the cost and margin groups are gone and the caller is
  // already printing the price itself. An empty bordered card beside the line
  // items would read as a panel that failed to load.
  if (suppressed) return null;

  return (
    <aside className={cn('space-y-4 rounded-lg border bg-card p-4', className)}>
      {showPriceSummary ? (
        <div className="space-y-1">
          <Row label="Price total" value={formatCurrency(grandTotal)} strong />
          <Row label="Sub-total" value={formatCurrency(subtotal)} muted />
          <Row label="Taxes" value={formatCurrency(taxTotal)} muted />
        </div>
      ) : null}

      {profitabilityHidden ? null : (
        <>
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cost
            </p>
            {laborCostRows(result).map((row) => (
              <Row key={row.label} label={row.label} value={formatCurrency(row.amount)} />
            ))}
            <Row label="Material" value={formatCurrency(result.material)} />
            <Row label="Equipment" value={formatCurrency(result.equipment)} />
            <Row label="Total cost" value={formatCurrency(result.totalCost)} strong />
          </div>

          {result.hoursMissingCostRate > 0 && (
            <p className="flex items-start gap-1.5 rounded-md border border-warning/30 bg-warning/10 p-2 text-xs text-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                {result.hoursMissingCostRate}{' '}
                {result.hoursMissingCostRate === 1 ? 'hour has' : 'hours have'} no cost rate, so the
                margin below is higher than the real one. Set the unit cost on those service lines.
              </span>
            </p>
          )}

          {/* Its own labelled group, exactly as Cost is. These two figures are what
          the business KEEPS — a different kind of statement from the price
          rows and from the cost rows, and the one an operator most needs to
          find at a glance. Unlabelled, they read as the tail of whatever list
          happened to precede them. */}
          <div className="space-y-1 border-t pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Margin
            </p>
            <div className="flex items-baseline justify-between gap-3 text-sm font-medium">
              <span>Gross margin</span>
              <span className={cn('tabular-nums', marginTone)}>
                {margin == null ? '—' : `${margin >= 0 ? '+' : ''}${margin.toFixed(2)}%`}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3 text-sm font-medium">
              <span>Net amount</span>
              <span className={cn('tabular-nums', marginTone)}>
                {result.netMarginAmount >= 0 ? '+' : ''}
                {formatCurrency(result.netMarginAmount)}
              </span>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}

/**
 * The Labor rows of the Cost group — one per KIND of labor, and their total
 * when there are two.
 *
 * Each row names what it is paid on: the hours for hourly labor, the percentage
 * for a margin share. Both kinds on one document read "Labor (7 hrs)",
 * "Labor (18% share)", "Labor total", because only one of the two moves when
 * the price does and a single figure hid which. A share with no single
 * percentage (two services paying different ones) says "share of margin".
 */
export function laborCostRows(result: {
  labor: number;
  laborFromMargin: number;
  laborSharePercent?: number | null;
  laborHours: number;
}): Array<{ label: string; amount: number }> {
  const hours = result.laborHours;
  const hourlyLabel = `Labor (${+hours.toFixed(2)} ${hours === 1 ? 'hr' : 'hrs'})`;
  const percent = result.laborSharePercent;
  const shareLabel =
    percent != null ? `Labor (${+percent.toFixed(2)}% share)` : 'Labor (share of margin)';

  if (result.laborFromMargin <= 0) {
    return [{ label: hours > 0 ? hourlyLabel : 'Labor', amount: result.labor }];
  }
  if (hours <= 0) return [{ label: shareLabel, amount: result.labor }];
  return [
    { label: hourlyLabel, amount: result.labor - result.laborFromMargin },
    { label: shareLabel, amount: result.laborFromMargin },
    { label: 'Labor total', amount: result.labor },
  ];
}
